import { useEffect, useState, type ReactNode } from 'react'
import { ScrollView, View } from 'react-native'
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import { borderRadius } from '@ui/theme/values'
import { useMaxWindowDimensions } from '@ui/theme/useRWD'

export type InsetSheetProps = {
  children: ReactNode
  childrenOutside?: ReactNode
  /** Reserved top region (e.g. a mask). A number is treated as height in px. */
  topSpace?: number | ReactNode
  /** Stretch to the parent width instead of the centered max-width cap. */
  fullWidth?: boolean
}

function TopSpace({ topSpace }: { topSpace?: number | ReactNode }) {
  if (topSpace == null) {
    return null
  }

  if (typeof topSpace === 'number') {
    return <View style={{ height: topSpace }} />
  }

  return topSpace
}

export default function InsetSheet({
  children,
  childrenOutside,
  topSpace,
  fullWidth = false,
}: InsetSheetProps) {
  const { width } = useMaxWindowDimensions()
  const translateY = useSharedValue(1000)
  const [cardHeight, setCardHeight] = useState(0)
  const [viewportHeight, setViewportHeight] = useState(0)

  const isOverflowing =
    viewportHeight > 0 && cardHeight > 0 && cardHeight > viewportHeight

  useEffect(() => {
    translateY.set(
      withTiming(0, {
        duration: 400,
        easing: Easing.out(Easing.cubic),
      })
    )
  }, [translateY])

  const slideStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }))

  return (
    <View className="absolute inset-0">
      <TopSpace topSpace={topSpace} />
      <View className="flex-1 overflow-hidden">
        <Animated.View
          className={fullWidth ? 'flex-1' : 'flex-1 self-center'}
          style={[!fullWidth && { width }, slideStyle]}
        >
          <View className="flex-1 overflow-hidden" style={{ borderRadius }}>
            <ScrollView
              className="flex-1"
              keyboardShouldPersistTaps="handled"
              onLayout={(event) => {
                setViewportHeight(event.nativeEvent.layout.height)
              }}
            >
              <View
                className="w-full"
                style={{
                  minHeight: viewportHeight || undefined,
                  justifyContent: isOverflowing ? 'flex-start' : 'flex-end',
                }}
              >
                <View
                  className="w-full overflow-hidden"
                  style={{ borderRadius }}
                  onLayout={(event) => {
                    setCardHeight(event.nativeEvent.layout.height)
                  }}
                >
                  {children}
                </View>
              </View>
            </ScrollView>
          </View>
        </Animated.View>
      </View>
      {childrenOutside}
    </View>
  )
}
